import { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Modal, Spin } from "antd";
import { useOrderingMessage } from "../../useOrderingMessage";
import { LoadingOutlined, EuroCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import {
  useActivePaymentMethods,
  usePaymentMethods,
} from "../../../../../hooks/usePaymentMethods";
import { useSplitAndPayBill } from "../../../../../hooks/useBills";
import { usePrinterNotifications } from "../../../../../hooks/usePrinterNotifications";
import { getBills } from "../../../../../api/bills";
import { getApiErrorMessage } from "../../../../../utils/translations";
import { useDiscountClients } from "../../../../../hooks/useDiscounts";
import { useViewport } from "../../../../../hooks/useViewport";
import { useCashChangeEnabled } from "../../../../../hooks/useConfiguration";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";
import { parseMoneyToCents } from "../../../../../utils/money";
import { useBillCloseTip } from "./useBillCloseTip";
import { isTipLayout } from "./tipDue";
import { ClosePaymentBody } from "./ClosePaymentBody";
import { usePaymentMethodKeys } from "./usePaymentMethodKeys";

interface SplitAndPayElement {
  billElementId: number;
  quantity: number;
}

interface SplitAndPayModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  billId: number;
  sourceTableName: string;
  elements: SplitAndPayElement[];
  totalAmount: number;
  canPaymentCash: boolean;
  canPaymentCard: boolean;
  canCloseBill1?: boolean;
  customerId?: number | null;
}

export const SplitAndPayModal = ({
  open,
  onClose,
  onSuccess,
  billId,
  sourceTableName,
  elements,
  totalAmount,
  canPaymentCash,
  canPaymentCard,
  canCloseBill1 = false,
  customerId = null,
}: SplitAndPayModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: rawMethods = [], isLoading } = useActivePaymentMethods();
  const { data: allMethods = [] } = usePaymentMethods();
  // id=3 (Смесено) се показва само ако имаме конфигурирани и id=1, и id=2.
  const methods = useMemo(() => {
    const hasCash = rawMethods.some((m) => m.id === 1);
    const hasCard = rawMethods.some((m) => m.id === 2);
    const withoutMixed = rawMethods.filter((m) => m.id !== 3);
    if (!hasCash || !hasCard) return withoutMixed;
    const mixed =
      rawMethods.find((m) => m.id === 3) ?? allMethods.find((m) => m.id === 3);
    if (!mixed) return withoutMixed;
    const cardIdx = withoutMixed.findIndex((m) => m.id === 2);
    const insertAt = cardIdx >= 0 ? cardIdx + 1 : withoutMixed.length;
    return [
      ...withoutMixed.slice(0, insertAt),
      mixed,
      ...withoutMixed.slice(insertAt),
    ];
  }, [rawMethods, allMethods]);
  const splitAndPayMutation = useSplitAndPayBill();
  const message = useOrderingMessage();
  const { notifyPrinterError, notifyPrinterTimeout } = usePrinterNotifications();
  const queryClient = useQueryClient();

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [submitting, setSubmitting] = useState(false);
  const [mixedCashAmount, setMixedCashAmount] = useState(0);
  const [mixedCardAmount, setMixedCardAmount] = useState(0);
  const [cashClickCount, setCashClickCount] = useState(0);
  // Полученото за ресто-помощника при „В брой" — само визуално, като в „Закрий".
  const [cashReceived, setCashReceived] = useState("");
  const cashChangeEnabled = useCashChangeEnabled();

  const cashOnlyMode = canCloseBill1 && cashClickCount >= 3;

  // Плащане по сметка (id=4) е ВИНАГИ видимо, но е активно САМО при избран
  // клиент с флаг can_pay_on_account (org DB) — иначе opacity + хинт.
  const { data: discountData } = useDiscountClients(open);
  const hasCustomer = customerId != null;
  const canOnAccount =
    hasCustomer &&
    (discountData?.items.find((c) => c.id === customerId)
      ?.can_pay_on_account ??
      false);

  // Ако клиентът отпадне/няма право докато id=4 е избран — върни към cash
  useEffect(() => {
    if (selectedPaymentMethodId === 4 && !canOnAccount) {
      setSelectedPaymentMethodId(1);
    }
  }, [canOnAccount, selectedPaymentMethodId]);

  const methodNameOf = (methodId: number) => {
    const method = allMethods.find((m) => m.id === methodId);
    return (i18n.language === "en" ? method?.name_en : method?.name_bg) ?? "";
  };

  // Бакшишът — същата секция и същите правила като в „Закрий"
  // (payment/useBillCloseTip.ts); при изключени настройки я няма.
  const closeTip = useBillCloseTip({
    open,
    isRetry: false,
    orderedAmount: totalAmount,
    activeMethods: rawMethods,
    methodNameOf,
    billMethodId: selectedPaymentMethodId,
    canPaymentCash,
    canPaymentCard,
    canOnAccount,
    cashOnlyMode,
    sendsNonFiscalTotal: cashOnlyMode || selectedPaymentMethodId === 4,
    onTipDropped: () => message.warning(t("closeTable.tip.dropped")),
  });
  const handleSubmitRef = useRef<() => void>(() => {});
  // Бърз двоен Enter/клик — `submitting` още не е стигнал до handler-а.
  const submitInFlightRef = useRef(false);

  // Кликнат ли е метод ръчно, авто-дефолтът долу спира да пипа избора
  const userPickedMethodRef = useRef(false);

  // Дефолтен метод при отваряне — като в „Закрий": „По сметка" (4) при
  // клиент с правото, иначе „В брой" (1). Данните за клиента идват async →
  // ефектът може да превключи 1 → 4 малко след отварянето, но САМО преди
  // ръчен избор.
  useEffect(() => {
    if (!open || methods.length === 0 || userPickedMethodRef.current) return;
    const defaultMethodId =
      canOnAccount && methods.some((m) => m.id === 4) ? 4 : 1;
    if (
      methods.some((m) => m.id === defaultMethodId) &&
      selectedPaymentMethodId !== defaultMethodId
    ) {
      setSelectedPaymentMethodId(defaultMethodId);
    }
  }, [open, methods, canOnAccount, selectedPaymentMethodId]);

  useEffect(() => {
    if (!open) {
      setSelectedPaymentMethodId(null);
      setMixedCashAmount(0);
      setMixedCardAmount(0);
      setCashClickCount(0);
      setCashReceived("");
      userPickedMethodRef.current = false;
    }
  }, [open]);

  // Клавишите — същите като в „Закрий" (`usePaymentMethodKeys`).
  usePaymentMethodKeys({
    enabled: open && isDesktop && !isLoading,
    methodIds: methods
      .filter((m) => {
        if (m.id === 1) return canPaymentCash;
        if (m.id === 2) return canPaymentCard;
        if (m.id === 3) return canPaymentCash && canPaymentCard;
        if (m.id === 4) return canOnAccount;
        return true;
      })
      .map((m) => m.id),
    selectedMethodId: selectedPaymentMethodId,
    onMove: (methodId) => {
      // Стрелките също са ръчен избор — иначе дефолтът би го върнал.
      userPickedMethodRef.current = true;
      // Като в „Закрий": друг метод прекъсва последователните кликове на „В брой".
      if (methodId !== 1) setCashClickCount(0);
      setSelectedPaymentMethodId(methodId);
    },
    onEnter: () => {
      if (!submitting && selectedPaymentMethodId) handleSubmitRef.current();
    },
    onEscape: () => {
      if (!submitting) onClose();
    },
  });

  handleSubmitRef.current = async () => {
    if (!selectedPaymentMethodId) {
      message.warning(t("closeTable.selectPaymentWarning"));
      return;
    }
    if (selectedPaymentMethodId === 3) {
      const sum = mixedCashAmount + mixedCardAmount;
      if (Math.abs(sum - totalAmount) > 0.01) {
        message.warning(
          t("closeTable.sumMismatch", {
            total: totalAmount.toFixed(2),
            entered: sum.toFixed(2),
          }),
        );
        return;
      }
    }
    if (submitInFlightRef.current) return;
    const tipWarning = closeTip.checkBeforeClose();
    if (tipWarning) {
      message.warning(tipWarning);
      return;
    }
    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      // Сметката може да е мръднала след избора (друг сервитьор, сторно) —
      // при самото натискане теглим свежите сметки (fetchQuery пълни и кеша
      // ["bills"]) и преизчисляваме сумата на избраните редове от тях.
      const freshBills = await queryClient.fetchQuery({
        queryKey: ["bills"],
        queryFn: getBills,
        staleTime: 0,
      });
      const freshBill = freshBills.find((bill) => bill.id === billId);
      const freshItems = freshBill?.elements.flatMap((el) => el.items) ?? [];
      let freshTotal = 0;
      let selectionValid = !!freshBill;
      for (const element of elements) {
        const freshItem = freshItems.find(
          (item) => item.id === element.billElementId,
        );
        if (!freshItem || parseFloat(freshItem.quantity) < element.quantity) {
          selectionValid = false;
          break;
        }
        const additionsTotal = freshItem.additions.reduce(
          (sum, addition) => sum + parseFloat(addition.total_sum),
          0,
        );
        const lineTotal = parseFloat(freshItem.total_sum) + additionsTotal;
        freshTotal +=
          (lineTotal / parseFloat(freshItem.quantity)) * element.quantity;
      }
      if (!selectionValid || Math.abs(freshTotal - totalAmount) > 0.005) {
        // Назад към избора на продукти — той вече показва свежите данни
        message.warning(t("splitBill.billChanged"));
        onClose();
        return;
      }

      // Бакшишът — БЕЗ `totalWithTip`: сумата на избраните редове тук е
      // оценка (BE закръгля всеки разделен ред отделно) и би дала фалшив
      // „bill_total_changed"; свежестта на избора е проверена горе.
      const tipOutcome = await closeTip.prepareForClose(
        queryClient,
        parseMoneyToCents(totalAmount) ?? 0,
        { includeTotalWithTip: false },
      );
      if (!tipOutcome.ok) {
        message.warning(tipOutcome.warning);
        return;
      }

      const result = await splitAndPayMutation.mutateAsync({
        billId,
        data: {
          elements,
          paymentMethodId: selectedPaymentMethodId,
          mixedCashAmount:
            selectedPaymentMethodId === 3 ? mixedCashAmount : undefined,
          mixedCardAmount:
            selectedPaymentMethodId === 3 ? mixedCardAmount : undefined,
          total: cashOnlyMode || selectedPaymentMethodId === 4 ? 0 : undefined,
          ...(tipOutcome.tip ?? {}),
        },
      });
      onSuccess();
      const fiscal = result?.fiscal_result;
      if (!fiscal || fiscal.status === "success") {
        message.success(t("splitBill.splitAndPaySuccess"));
      } else if (fiscal.status === "timeout") {
        notifyPrinterTimeout(fiscal.error || t("closeTable.printerTimeout"));
      } else {
        notifyPrinterError(fiscal.error || t("closeTable.printerError"));
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, t("splitBill.splitAndPayError")));
      console.error("Split & pay error:", error);
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  // Изгледът е 1:1 с „Закрий" (общото тяло `ClosePaymentBody`, същите ширини).
  const tipLayout = isTipLayout(closeTip);
  const totalText = totalAmount.toFixed(2);
  const changeMixedCash = (cash: number) => {
    setMixedCashAmount(cash);
    setMixedCardAmount(Math.max(0, totalAmount - cash));
  };
  const changeMixedCard = (card: number) => {
    setMixedCardAmount(card);
    setMixedCashAmount(Math.max(0, totalAmount - card));
  };
  // „Смесено" на широк екран: бутонът чака сборът да покрие сумата (както в „Закрий").
  const mixedIncomplete =
    !isNarrow &&
    selectedPaymentMethodId === 3 &&
    Math.abs(mixedCashAmount + mixedCardAmount - totalAmount) > 0.01;
  const width = isMobile ? undefined : isTablet ? 560 : 880;

  return (
    <Modal
      open={open}
      onCancel={() => !submitting && onClose()}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        className="bg-pos-card font-pos-sans text-pos-ink"
        onClick={() => {
          if (cashOnlyMode) setCashClickCount(0);
        }}
      >
        <ModalHeader
          icon={<EuroCircleOutlined />}
          title={t("splitBill.splitAndPayTitle")}
          subtitle={t("splitBill.splitAndPaySubtitle", {
            name: sourceTableName,
          })}
          onClose={() => !submitting && onClose()}
          isNarrow={isNarrow}
          tone="panel"
        />

        {isLoading ? (
          <div className="flex items-center justify-center px-6 py-12">
            <Spin
              indicator={<LoadingOutlined className="text-[36px] text-pos-accent" spin />}
            />
          </div>
        ) : (
          <>
            <ClosePaymentBody
              closeTip={closeTip}
              tipLayout={tipLayout}
              methods={methods}
              selectedPaymentMethodId={selectedPaymentMethodId}
              onPickMethod={(methodId, e) => {
                userPickedMethodRef.current = true;
                if (methodId === 1 && canCloseBill1) {
                  e.stopPropagation();
                  setCashClickCount((c) => c + 1);
                } else {
                  setCashClickCount(0);
                }
                setSelectedPaymentMethodId(methodId);
              }}
              totalAmount={totalText}
              methodNameOf={methodNameOf}
              cashChangeEnabled={cashChangeEnabled}
              cashReceived={cashReceived}
              onCashReceivedChange={setCashReceived}
              // Цените на избраните редове вече са след клиентската отстъпка.
              clientDiscount={null}
              mixedCashAmount={mixedCashAmount}
              mixedCardAmount={mixedCardAmount}
              onMixedCashChange={changeMixedCash}
              onMixedCardChange={changeMixedCard}
              canPaymentCash={canPaymentCash}
              canPaymentCard={canPaymentCard}
              canOnAccount={canOnAccount}
              cashOnlyMode={cashOnlyMode}
              isNarrow={isNarrow}
              disabled={submitting}
            />

            <ModalFooter isNarrow={isNarrow} tone="panel" narrowFirst>
              <GhostButton
                label={t("common.back")}
                onClick={onClose}
                disabled={submitting}
                shortcut={isDesktop ? "Esc" : undefined}
              />
              <PrimaryButton
                label={t("splitBill.splitAndPayButton")}
                onClick={() => handleSubmitRef.current()}
                disabled={
                  !selectedPaymentMethodId ||
                  submitting ||
                  mixedIncomplete ||
                  closeTip.blocksClose
                }
                loading={submitting}
                shortcut={isDesktop ? "⏎" : undefined}
              />
            </ModalFooter>
          </>
        )}
      </div>
    </Modal>
  );
};
